import { GS1_PRIMARY_KEY_OPTIONS, type PrimaryKeyOption } from '@myplayer/gs1';
import {
  LINK_TYPE_OPTIONS,
  isIdentifierRecalled,
  type IdentifierDisplayStatus,
  type IdentifierRecord,
  type ScanAnalyticsDailyDocument,
  type StagedDraftRecord,
} from '@myplayer/schema';
import { IconProducts, IconSearch } from '@myplayer/ui';
import { type ReactElement } from 'react';
import { RailActionMenu, useInsideAdminShell } from './App.js';
import { Button, ModalDialog, ToastNotification } from './components/ui/primitives.js';
import type {
  GetWorkspaceScanAnalyticsCallablePayload,
  GetWorkspaceScanAnalyticsCallableResult,
} from './firebase.js';
import { ProductAdvancedLinksSection } from './product-advanced-links-section.js';
import { ProductCreateForm } from './product-create-form.js';
import { ProductDetailPanel } from './product-detail-panel.js';
import { ProductLinksEditor, ProductScheduleSection } from './product-links-editor.js';
import { ProductListCard } from './product-list-card.js';
import { type QualifierOverrideRecord } from './product-qualifiers-helpers.js';
import { ProductQualifiersSection } from './product-qualifiers-section.js';
import { ProductDrawerNavActions, ProductQrCard } from './product-qr-card.js';
import { ProductRecallSection } from './product-recall-section.js';
import {
  ProductScanOverviewCard,
  type ProductScanOverviewCardProps,
} from './product-scan-overview-card.js';
import { ProductsCsvPanel } from './products-csv-panel.js';
import { ProductsTable } from './products-table.js';
import { openQrCheckInNewTab } from './qr-check-helper.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { useProductsController, type ProductsControllerParams } from './use-products-controller.js';
export type {
  CancelScheduledChangeCallablePayload,
  CancelScheduledChangeCallableResult,
  ClearRecallCallablePayload,
  ClearRecallCallableResult,
  SetRecallCallablePayload,
  SetRecallCallableResult,
  SetScheduledChangeCallablePayload,
  SetScheduledChangeCallableResult,
} from './firebase.js';
export type { IdentifierDisplayStatus, IdentifierRecord, PrimaryKeyOption, StagedDraftRecord };
export * from './product-qualifiers-helpers.js';
export * from './products-csv.js';
export * from './products-firestore.js';
export {
  GS1_PRIMARY_KEY_OPTIONS,
  LINK_TYPE_OPTIONS,
  ProductScanOverviewCard,
  isIdentifierRecalled,
  type ProductScanOverviewCardProps,
};

export interface ProductsPageProps extends ProductsControllerParams {
  readonly initialQualifiers?: readonly QualifierOverrideRecord[] | undefined;
  readonly initialDailyRecords?: readonly ScanAnalyticsDailyDocument[] | undefined;
  readonly initialRollupResult?: GetWorkspaceScanAnalyticsCallableResult | undefined;
  readonly referenceDay?: string | undefined;
  readonly onFetchAnalytics?:
    | ((
        payload: GetWorkspaceScanAnalyticsCallablePayload,
      ) => Promise<GetWorkspaceScanAnalyticsCallableResult>)
    | undefined;
}

export function ProductsPage(props: ProductsPageProps = {}): ReactElement {
  const {
    defaultPageSize,
    initialQualifiers,
    initialDailyRecords,
    initialRollupResult,
    referenceDay,
    onFetchAnalytics,
  } = props;
  const ctrl = useProductsController(props);
  const insideAdminShell = useInsideAdminShell();
  const selectedTitle =
    ctrl.selectedRecord?.draft?.title ??
    ctrl.selectedRecord?.title ??
    ctrl.selectedRecord?.primaryKey ??
    '';
  const isSearchOpen =
    ctrl.showSearchBar ||
    ctrl.searchQuery.trim().length > 0 ||
    ctrl.brandFilter !== 'ALL' ||
    ctrl.statusFilter !== 'ALL' ||
    ctrl.sectorFilter !== 'ALL' ||
    ctrl.validationFilter !== 'ALL' ||
    ctrl.workspaceFilter !== 'ALL';
  const handleToggleSearch = (): void => {
    ctrl.setShowSearchBar(!isSearchOpen);
    if (isSearchOpen) {
      ctrl.setSearchQuery('');
      ctrl.setBrandFilter('ALL');
      ctrl.setStatusFilter('ALL');
      ctrl.setSectorFilter('ALL');
      ctrl.setValidationFilter('ALL');
      ctrl.setWorkspaceFilter('ALL');
    }
  };
  const sharedListProps = {
    ...ctrl,
    selectedIdentifierKey: ctrl.selectedRecord?.identifierKey,
    showWorkspaceBadges: ctrl.isMultiWorkspaceQuery,
    showSearchBar: isSearchOpen,
    onSelectRecord: ctrl.handleOpenProduct,
    onEditRecord: (identifierKey: string) => {
      ctrl.handleOpenProduct(identifierKey);
      ctrl.setIsEditing(true);
      ctrl.pushProductPath(`/products/${encodeURIComponent(identifierKey)}/edit`);
    },
  };
  const activeToggleCls = 'border-brand bg-brand-wash text-brand';
  const handleMarkLinkRemoved = async (idx: number): Promise<boolean> => {
    const target = ctrl.workingLinks[idx];
    const activeLinksCount = ctrl.workingLinks.filter((l) => l.removed !== true).length;
    if (!target || target.defaultLink || activeLinksCount <= 1) {
      ctrl.setLinkFormError(
        'Set another link as the main scan destination before removing this link.',
      );
      return false;
    }
    return ctrl.applyDraftLinksUpdate(
      ctrl.workingLinks.map((l, i) =>
        i === idx ? { ...l, removed: true, defaultLink: false } : l,
      ),
    );
  };
  const handleRestoreLink = (idx: number): Promise<boolean> =>
    ctrl.applyDraftLinksUpdate(
      ctrl.workingLinks.map((l, i) => {
        if (i !== idx) return l;
        const { removed: _removed, ...rest } = l;
        return rest;
      }),
    );

  return (
    <>
      <div className="space-y-4">
        {ctrl.routerSyncElement}
        {!insideAdminShell && (
          <RailActionMenu
            canCreateIdentifier={ctrl.canEdit}
            canExportIdentifier={ctrl.canExport}
            onAddProduct={() => {
              ctrl.setShowNewForm(!ctrl.showNewForm);
              ctrl.setNewFormError(null);
            }}
            onImportCsv={() => {
              ctrl.setShowCsvImportPanel(!ctrl.showCsvImportPanel);
              ctrl.setCsvImportError(null);
              ctrl.setCsvImportSuccess(null);
            }}
            onExportCsv={ctrl.handleExportCsv}
            onCheckPrintedQr={() => openQrCheckInNewTab()}
          />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-page-title">Products</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              data-testid="toggle-search-btn"
              aria-label="Toggle search"
              aria-expanded={isSearchOpen}
              title={isSearchOpen ? 'Hide search and filters' : 'Show search and filters'}
              onClick={handleToggleSearch}
              className={isSearchOpen ? activeToggleCls : undefined}
            >
              <IconSearch />
            </Button>
          </div>
        </div>

        {!ctrl.canEdit && (
          <div
            role="status"
            data-testid="products-readonly-notice"
            className="rounded-control border border-border bg-surface-muted px-3.5 py-2.5 text-[12px] text-fg-secondary"
          >
            {formatCapabilityRestrictionNotice({
              capability: 'identifier:create_stage_update',
              actionSummary: 'create products, import CSVs, or stage link changes',
              callerRole: ctrl.session?.personaRole,
            })}
          </div>
        )}

        {ctrl.listenerError !== null && (
          <div
            role="alert"
            data-testid="products-listener-error"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {ADMIN_STRINGS.errors.productsListenerPrefix}{' '}
            {formatPlainAdminError(
              ctrl.listenerError,
              ADMIN_STRINGS.errors.productsListenerFallback,
            )}
          </div>
        )}

        {ctrl.showCsvImportPanel && ctrl.canEdit && (
          <ProductsCsvPanel
            {...ctrl}
            onValidateCsv={ctrl.handleValidateCsv}
            onStageValidRows={ctrl.handleStageValidCsvRows}
            onClose={() => {
              ctrl.setShowCsvImportPanel(false);
              ctrl.setCsvImportError(null);
              ctrl.setCsvImportSuccess(null);
            }}
          />
        )}

        {ctrl.showNewForm && ctrl.canEdit && (
          <ProductCreateForm
            {...ctrl}
            showWorkspaceSelector={
              ctrl.workspaceScope === 'ALL' || ctrl.availableWorkspaces.length > 1
            }
            onSubmit={ctrl.handleCreateProduct}
            onCancel={() => {
              ctrl.setShowNewForm(false);
              ctrl.setNewFormError(null);
            }}
          />
        )}

        {ctrl.viewMode === 'table' ? (
          <ProductsTable
            records={ctrl.filteredRecords}
            {...sharedListProps}
            {...(defaultPageSize !== undefined ? { defaultPageSize } : {})}
          />
        ) : (
          <ProductListCard {...sharedListProps} />
        )}
      </div>

      {ctrl.selectedRecord === null && ctrl.linkFormSuccess !== null && (
        <ToastNotification
          data-testid="product-save-confirmation"
          message={<span data-testid="link-editor-success">{ctrl.linkFormSuccess}</span>}
          onDismiss={() => ctrl.setLinkFormSuccess(null)}
        />
      )}

      {ctrl.selectedRecord !== null && (
        <ModalDialog
          open={true}
          presentation="slide-up"
          title={selectedTitle || 'Product details'}
          subtitle={
            ctrl.isMultiWorkspaceQuery && ctrl.selectedRecord.workspaceId
              ? `GTIN ${ctrl.selectedRecord.primaryKey} · ${ctrl.workspaceNameMap.get(ctrl.selectedRecord.workspaceId) ?? ctrl.selectedRecord.workspaceId}`
              : `GTIN ${ctrl.selectedRecord.primaryKey}`
          }
          icon={<IconProducts />}
          maxWidthClassName="max-w-none"
          disableEscapeClose={
            ctrl.isEditing ||
            ctrl.isScheduleModalOpen ||
            ctrl.isExtraLinkModalOpen ||
            ctrl.showRecallConfirm
          }
          closeButtonTestId="back-to-products-button"
          headerActions={
            <ProductDrawerNavActions
              selectedRecord={ctrl.selectedRecord}
              filteredRecords={ctrl.filteredRecords}
              disabled={ctrl.isEditing}
              onSelectRecord={ctrl.handleOpenProduct}
            />
          }
          onClose={ctrl.handleBackToProducts}
          data-testid="product-page-view"
        >
          <div className="w-full space-y-4">
            {ctrl.linkFormSuccess !== null &&
              ctrl.selectedRecord.validation?.status !== 'INVALID' &&
              !ctrl.isEditing &&
              !ctrl.isScheduleModalOpen &&
              !ctrl.isExtraLinkModalOpen && (
                <ToastNotification
                  data-testid="product-save-confirmation"
                  message={<span data-testid="link-editor-success">{ctrl.linkFormSuccess}</span>}
                  onDismiss={() => ctrl.setLinkFormSuccess(null)}
                />
              )}

            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
              <div className="min-w-0 space-y-4 lg:col-span-7">
                <ProductDetailPanel
                  selectedRecord={ctrl.selectedRecord}
                  effectiveResolverHost={ctrl.effectiveResolverHost}
                  canEdit={ctrl.canEdit}
                  isCheckingPromotion={ctrl.isCheckingPromotion}
                  discardingDraft={ctrl.discardingDraft}
                  discardDraftError={ctrl.discardDraftError}
                  duplicateConflicts={ctrl.duplicateConflicts}
                  onStartEdit={ctrl.handleStartEditingProduct}
                  onDiscardDraft={ctrl.handleDiscardDraft}
                />
                <ProductScanOverviewCard
                  selectedRecord={ctrl.selectedRecord}
                  isControlled={props.initialRecords !== undefined}
                  initialDailyRecords={initialDailyRecords}
                  initialRollupResult={initialRollupResult}
                  referenceDay={referenceDay}
                  onFetchAnalytics={onFetchAnalytics}
                />
              </div>
              <div className="min-w-0 space-y-4 lg:col-span-5">
                <ProductQrCard
                  selectedRecord={ctrl.selectedRecord}
                  effectiveResolverHost={ctrl.effectiveResolverHost}
                  qrTableId={ctrl.qrTableId}
                  setQrTableId={ctrl.setQrTableId}
                  currentQrPreview={ctrl.currentQrPreview}
                  lastDownloadedQr={ctrl.lastDownloadedQr}
                  onDownloadQr={ctrl.handleDownloadQr}
                  onOpenQrCheck={() => openQrCheckInNewTab(ctrl.selectedRecord?.identifierKey)}
                />
                <ProductAdvancedLinksSection
                  {...ctrl}
                  selectedRecord={ctrl.selectedRecord}
                  onOpenAddLinkModal={ctrl.handleOpenAddLinkModal}
                  onStartEditLink={ctrl.handleStartEditLink}
                  onSetMainLink={ctrl.handleSetMainLink}
                  onScheduleLink={ctrl.handleOpenScheduleForUrl}
                  onMarkLinkRemoved={handleMarkLinkRemoved}
                  onRestoreLink={handleRestoreLink}
                  onSaveLinkForm={ctrl.handleSaveLinkForm}
                  onCancelEdit={ctrl.resetLinkForm}
                />
                <ProductScheduleSection
                  {...ctrl}
                  selectedRecord={ctrl.selectedRecord}
                  onOpenScheduleModal={ctrl.handleOpenScheduleModal}
                  onCloseScheduleModal={ctrl.handleCloseScheduleModal}
                  onSaveSchedule={ctrl.handleSaveSchedule}
                  onClearSchedule={ctrl.handleClearSchedule}
                />
                <ProductQualifiersSection
                  selectedRecord={ctrl.selectedRecord}
                  session={ctrl.session}
                  canEdit={ctrl.canEdit}
                  isControlled={props.initialRecords !== undefined}
                  initialQualifiers={initialQualifiers}
                  effectiveResolverHost={ctrl.effectiveResolverHost}
                  onSaved={(msg) => ctrl.setLinkFormSuccess(msg)}
                />
                <ProductRecallSection
                  {...ctrl}
                  selectedRecord={ctrl.selectedRecord}
                  onConfirmSetRecall={ctrl.handleConfirmSetRecall}
                  onClearRecall={ctrl.handleClearRecall}
                />
              </div>
            </div>
          </div>
        </ModalDialog>
      )}

      {ctrl.selectedRecord !== null && ctrl.isEditing && (
        <ProductLinksEditor
          productTitle={selectedTitle}
          productGtin={ctrl.selectedRecord.primaryKey}
          workingLinks={ctrl.workingLinks}
          editPrimaryUrl={ctrl.editPrimaryUrl}
          setEditPrimaryUrl={ctrl.setEditPrimaryUrl}
          editSector={ctrl.editSector}
          setEditSector={ctrl.setEditSector}
          canEdit={ctrl.canEdit}
          savingDraft={ctrl.savingDraft}
          linkFormError={ctrl.linkFormError}
          linkFormSuccess={ctrl.linkFormSuccess}
          effectiveResolverHost={ctrl.effectiveResolverHost}
          onSaveProductEdit={ctrl.handleSaveProductEdit}
          onCancelProductEdit={ctrl.handleCancelEditingProduct}
        />
      )}
    </>
  );
}
